import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, IconButton, Button, CircularProgress, Alert, Tooltip, Snackbar,
  TextField, InputAdornment, Chip, MenuItem, Select, FormControl, InputLabel,
  Pagination, Switch, FormControlLabel, Autocomplete, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import MedicationIcon from "@mui/icons-material/Medication";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { StyledDialog, inputStyles } from "./common";

const API_BASE = "/api";
const PAGE_SIZE = 50;

function authHeaders() {
  const userId = localStorage.getItem("user_id");
  const h = { "Content-Type": "application/json" };
  if (userId) h["X-User-Id"] = String(userId);
  return h;
}

export default function AdminVademecum() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [paises, setPaises] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);

  // Filtros
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filtroCat, setFiltroCat] = useState("");
  const [filtroPais, setFiltroPais] = useState("");
  const [filtroControlado, setFiltroControlado] = useState("");
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [page, setPage] = useState(1);

  // Dialog crear/editar
  const [dialog, setDialog] = useState(null);

  // Selección masiva — Set de IDs seleccionados (mantenidos en memoria mientras
  // estés en la misma sesión; se preservan al cambiar de página/filtro)
  const [seleccion, setSeleccion] = useState(() => new Set());
  // Dialog de confirmación de acción masiva: { action, count, hard }
  const [bulkDialog, setBulkDialog] = useState(null);

  const toggleSeleccion = (id) => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSeleccionarPagina = () => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      const idsPagina = items.map((it) => it.id);
      const todosSelec = idsPagina.every((id) => next.has(id));
      if (todosSelec) {
        idsPagina.forEach((id) => next.delete(id));
      } else {
        idsPagina.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const limpiarSeleccion = () => setSeleccion(new Set());

  async function ejecutarBulkAction(action, hard = false) {
    const ids = Array.from(seleccion);
    if (ids.length === 0) return;
    try {
      const r = await fetch(`${API_BASE}/admin/vademecum/_bulk`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ action, ids, hard }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `Error ${r.status}`);
      const labelMap = {
        deactivate: "desactivados",
        reactivate: "reactivados",
        delete: hard ? "eliminados permanentemente" : "desactivados",
      };
      setSnack({
        severity: "success",
        msg: `${data.affected} medicamento(s) ${labelMap[action] || "procesados"}`,
      });
      limpiarSeleccion();
      setBulkDialog(null);
      cargar();
    } catch (e) {
      setSnack({ severity: "error", msg: e.message });
      setBulkDialog(null);
    }
  }

  // Debounce buscador (350ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Reset page al cambiar filtros
  useEffect(() => { setPage(1); }, [debouncedQ, filtroCat, filtroPais, filtroControlado, mostrarInactivos]);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, filtroCat, filtroPais, filtroControlado, mostrarInactivos, page]);

  async function cargar() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set("q", debouncedQ);
      if (filtroCat) params.set("categoria", filtroCat);
      if (filtroPais) params.set("pais", filtroPais);
      if (filtroControlado) params.set("controlado", filtroControlado === "yes" ? "true" : "false");
      if (!mostrarInactivos) params.set("activo", "true");
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String((page - 1) * PAGE_SIZE));

      const r = await fetch(`${API_BASE}/admin/vademecum?${params}`, { headers: authHeaders() });
      if (!r.ok) {
        if (r.status === 403) throw new Error("Solo NARA Tech puede gestionar el vademécum");
        throw new Error(`Error ${r.status}`);
      }
      const data = await r.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPaises(data.paises || []);
      setCategorias(data.categorias || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function eliminar(item, hard = false) {
    const msg = hard
      ? `¿Eliminar PERMANENTEMENTE "${item.nombre_comercial}"?\nFalla si tiene referencias en recetas.`
      : `¿Desactivar "${item.nombre_comercial}"? Quedará oculto pero se conserva el historial.`;
    if (!window.confirm(msg)) return;
    try {
      const url = `${API_BASE}/admin/vademecum/${item.id}${hard ? "?hard=true" : ""}`;
      const r = await fetch(url, { method: "DELETE", headers: authHeaders() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail || `Error ${r.status}`);
      }
      setSnack({ severity: "success", msg: hard ? "Eliminado permanentemente" : "Desactivado" });
      cargar();
    } catch (e) {
      setSnack({ severity: "error", msg: e.message });
    }
  }

  async function reactivar(item) {
    try {
      const r = await fetch(`${API_BASE}/admin/vademecum/${item.id}/reactivar`, {
        method: "POST", headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      setSnack({ severity: "success", msg: "Reactivado" });
      cargar();
    } catch (e) {
      setSnack({ severity: "error", msg: e.message });
    }
  }

  async function guardar(payload, modo, id) {
    try {
      const url = modo === "crear"
        ? `${API_BASE}/admin/vademecum`
        : `${API_BASE}/admin/vademecum/${id}`;
      const method = modo === "crear" ? "POST" : "PUT";
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data?.detail === "string" ? data.detail : `Error ${r.status}`);
      setSnack({ severity: "success", msg: modo === "crear" ? "Medicamento creado" : "Medicamento actualizado" });
      setDialog(null);
      cargar();
    } catch (e) {
      setSnack({ severity: "error", msg: e.message });
    }
  }

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          background: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
          display: "grid", placeItems: "center", color: "#fff",
        }}>
          <MedicationIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
            Vademécum
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
            Catálogo maestro de medicamentos · {total.toLocaleString()} registros
          </Typography>
        </Box>
        <Tooltip title="Recargar"><IconButton onClick={cargar}><RefreshIcon /></IconButton></Tooltip>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialog({
            modo: "crear",
            data: {
              nombre_comercial: "", nombre_generico: "", principio_activo: "",
              presentacion: "", concentracion: "", forma_farmaceutica: "",
              via_administracion: "", laboratorio: "", categoria: "",
              requiere_receta: true, controlado: false, pais: "ALL", activo: true,
            },
          })}
          sx={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)", textTransform: "none", fontWeight: 700 }}
        >
          Agregar
        </Button>
      </Box>

      {/* Filtros */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Buscar nombre, genérico, principio activo o laboratorio..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Categoría</InputLabel>
          <Select value={filtroCat} label="Categoría" onChange={(e) => setFiltroCat(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {categorias.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>País</InputLabel>
          <Select value={filtroPais} label="País" onChange={(e) => setFiltroPais(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {paises.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Controlado</InputLabel>
          <Select value={filtroControlado} label="Controlado" onChange={(e) => setFiltroControlado(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="yes">Sí, controlado</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch size="small" checked={mostrarInactivos} onChange={(e) => setMostrarInactivos(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13 }}>Mostrar inactivos</Typography>}
        />
      </Box>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="warning">{error}</Alert>
      ) : items.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "#64748b" }}>
          <Typography>Sin resultados.</Typography>
        </Box>
      ) : (
        <>
          {/* Toolbar contextual de selección masiva */}
          {seleccion.size > 0 && (
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.5, mb: 2,
              p: 1.2, px: 2, borderRadius: 2,
              bgcolor: "#1e3a5f", color: "#fff",
              boxShadow: "0 4px 16px rgba(30, 58, 95, 0.3)",
              flexWrap: "wrap",
            }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 200 }}>
                {seleccion.size} medicamento{seleccion.size === 1 ? "" : "s"} seleccionado{seleccion.size === 1 ? "" : "s"}
              </Typography>
              <Button
                size="small"
                startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                onClick={() => setBulkDialog({ action: "deactivate", count: seleccion.size })}
                sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
              >
                Desactivar
              </Button>
              <Button
                size="small"
                startIcon={<RestoreIcon sx={{ fontSize: 16 }} />}
                onClick={() => setBulkDialog({ action: "reactivate", count: seleccion.size })}
                sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
              >
                Reactivar
              </Button>
              <Button
                size="small"
                startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                onClick={() => setBulkDialog({ action: "delete", count: seleccion.size, hard: true })}
                sx={{ bgcolor: "#dc2626", color: "#fff", textTransform: "none", "&:hover": { bgcolor: "#b91c1c" } }}
              >
                Eliminar permanente
              </Button>
              <Button size="small" onClick={limpiarSeleccion} sx={{ color: "#fff", textTransform: "none" }}>
                Cancelar
              </Button>
            </Box>
          )}

          <Box sx={{ display: "grid", gap: 0.6 }}>
            {/* Header columnas (desktop) */}
            <Box sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: "44px 1.6fr 1.4fr 1fr 110px 80px 100px 110px",
              gap: 1, px: 1.5, py: 0.5,
              color: "#64748b", fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
              textTransform: "uppercase",
              alignItems: "center",
            }}>
              <Checkbox
                size="small"
                checked={items.length > 0 && items.every((it) => seleccion.has(it.id))}
                indeterminate={items.some((it) => seleccion.has(it.id)) && !items.every((it) => seleccion.has(it.id))}
                onChange={toggleSeleccionarPagina}
                sx={{ p: 0.5 }}
              />
              <span>Comercial / Genérico</span>
              <span>Principio activo</span>
              <span>Presentación</span>
              <span>Categoría</span>
              <span>País</span>
              <span>Estado</span>
              <span style={{ textAlign: "right" }}>Acciones</span>
            </Box>
            {items.map((it) => (
              <Row key={it.id} item={it}
                selected={seleccion.has(it.id)}
                onToggleSelect={() => toggleSeleccion(it.id)}
                onEdit={() => setDialog({ modo: "editar", data: it })}
                onSoftDelete={() => eliminar(it, false)}
                onHardDelete={() => eliminar(it, true)}
                onReactivar={() => reactivar(it)}
              />
            ))}
          </Box>
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {dialog && (
        <MedicamentoDialog
          modo={dialog.modo}
          data={dialog.data}
          paises={paises}
          categorias={categorias}
          onClose={() => setDialog(null)}
          onSave={(payload) => guardar(payload, dialog.modo, dialog.data.id)}
        />
      )}

      {/* Dialog de confirmación de acción masiva */}
      <Dialog open={!!bulkDialog} onClose={() => setBulkDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1e3a5f" }}>
          {bulkDialog?.action === "delete" && "¿Eliminar permanentemente?"}
          {bulkDialog?.action === "deactivate" && "¿Desactivar medicamentos?"}
          {bulkDialog?.action === "reactivate" && "¿Reactivar medicamentos?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {bulkDialog?.action === "delete" && (
              <>
                Vas a <strong>eliminar permanentemente</strong> <strong>{bulkDialog?.count}</strong> medicamento(s) de la base de datos.
                <br /><br />
                Esta acción <strong>no se puede deshacer</strong>. Si alguno tiene referencias (recetas firmadas, etc.) la operación fallará para todo el lote.
                <br /><br />
                Si solo quieres ocultarlos, usa <strong>Desactivar</strong> en su lugar.
              </>
            )}
            {bulkDialog?.action === "deactivate" && (
              <>
                Vas a desactivar <strong>{bulkDialog?.count}</strong> medicamento(s). Quedarán ocultos en búsquedas pero el historial se conserva. Puedes reactivarlos después.
              </>
            )}
            {bulkDialog?.action === "reactivate" && (
              <>
                Vas a reactivar <strong>{bulkDialog?.count}</strong> medicamento(s). Volverán a aparecer en búsquedas y al recetar.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkDialog(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color={bulkDialog?.action === "delete" ? "error" : "primary"}
            onClick={() => ejecutarBulkAction(bulkDialog.action, !!bulkDialog.hard)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {bulkDialog?.action === "delete" && "Eliminar permanente"}
            {bulkDialog?.action === "deactivate" && "Desactivar"}
            {bulkDialog?.action === "reactivate" && "Reactivar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

function Row({ item, selected, onToggleSelect, onEdit, onSoftDelete, onHardDelete, onReactivar }) {
  return (
    <Box sx={{
      display: { xs: "block", md: "grid" },
      gridTemplateColumns: "44px 1.6fr 1.4fr 1fr 110px 80px 100px 110px",
      gap: 1, alignItems: "center",
      p: 1.2, borderRadius: 1.6,
      bgcolor: selected ? "#eff6ff" : "#fff",
      border: selected ? "1px solid #93c5fd" : "1px solid #e2e8f0",
      opacity: item.activo ? 1 : 0.55,
      transition: "background 0.15s, border-color 0.15s",
    }}>
      <Checkbox
        size="small"
        checked={!!selected}
        onChange={onToggleSelect}
        sx={{ p: 0.5, justifySelf: "start" }}
      />
      <Box>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.25 }}>
          {item.nombre_comercial}
          {item.controlado && (
            <Tooltip title="Controlado">
              <WarningAmberIcon sx={{ fontSize: 14, color: "#dc2626", ml: 0.5, verticalAlign: "middle" }} />
            </Tooltip>
          )}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
          {item.nombre_generico}
          {item.laboratorio ? ` · ${item.laboratorio}` : ""}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "#475569" }}>{item.principio_activo || "—"}</Typography>
      <Typography sx={{ fontSize: 12.5, color: "#475569" }}>
        {[item.concentracion, item.forma_farmaceutica, item.via_administracion].filter(Boolean).join(" · ") || "—"}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>{item.categoria || "—"}</Typography>
      <Chip size="small" label={item.pais || "ALL"} sx={{ bgcolor: "#eff6ff", color: "#1e3a5f", height: 22, justifySelf: "start" }} />
      <Chip
        size="small"
        label={item.activo ? "Activo" : "Inactivo"}
        color={item.activo ? "success" : "default"}
        variant={item.activo ? "filled" : "outlined"}
        sx={{ justifySelf: "start", height: 22 }}
      />
      <Box sx={{ display: "flex", gap: 0.4, justifyContent: { md: "flex-end" } }}>
        <Tooltip title="Editar"><IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton></Tooltip>
        {item.activo ? (
          <Tooltip title="Desactivar"><IconButton size="small" onClick={onSoftDelete} sx={{ color: "#dc2626" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        ) : (
          <>
            <Tooltip title="Reactivar"><IconButton size="small" onClick={onReactivar} sx={{ color: "#16a34a" }}><RestoreIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Eliminar permanente"><IconButton size="small" onClick={onHardDelete} sx={{ color: "#b91c1c" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}

function MedicamentoDialog({ modo, data, paises, categorias, onClose, onSave }) {
  const [form, setForm] = useState({ ...data });
  const [busy, setBusy] = useState(false);
  const [paisesSoportados, setPaisesSoportados] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Cargar lista cerrada de países (con flag y label) al abrir el dialog
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/admin/vademecum/_paises`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : [])
      .then((d) => { if (!cancelled) setPaisesSoportados(d || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const valid = (form.nombre_comercial || "").trim() && (form.nombre_generico || "").trim();

  return (
    <StyledDialog
      open
      onClose={busy ? undefined : onClose}
      title={modo === "crear" ? "Nuevo medicamento" : `Editar #${data.id}`}
      icon={<MedicationIcon />}
      maxWidth="md"
    >
      <StyledDialog.Body>
        <StyledDialog.FormSection>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <StyledDialog.FormField label="Nombre comercial" required>
              <TextField fullWidth size="small" sx={inputStyles}
                value={form.nombre_comercial || ""}
                onChange={(e) => set("nombre_comercial", e.target.value)}
                placeholder="Ej. Tylenol, Aspirina"
                inputProps={{ maxLength: 255 }}
              />
            </StyledDialog.FormField>
            <StyledDialog.FormField label="Nombre genérico" required>
              <TextField fullWidth size="small" sx={inputStyles}
                value={form.nombre_generico || ""}
                onChange={(e) => set("nombre_generico", e.target.value)}
                placeholder="Ej. Paracetamol"
                inputProps={{ maxLength: 255 }}
              />
            </StyledDialog.FormField>

            <StyledDialog.FormField label="Principio activo">
              <TextField fullWidth size="small" sx={inputStyles}
                value={form.principio_activo || ""}
                onChange={(e) => set("principio_activo", e.target.value)}
                inputProps={{ maxLength: 255 }}
              />
            </StyledDialog.FormField>
            <StyledDialog.FormField label="Laboratorio">
              <TextField fullWidth size="small" sx={inputStyles}
                value={form.laboratorio || ""}
                onChange={(e) => set("laboratorio", e.target.value)}
                inputProps={{ maxLength: 100 }}
              />
            </StyledDialog.FormField>

            <StyledDialog.FormField label="Concentración">
              <TextField fullWidth size="small" sx={inputStyles}
                value={form.concentracion || ""}
                onChange={(e) => set("concentracion", e.target.value)}
                placeholder="Ej. 500 mg / 5 ml"
                inputProps={{ maxLength: 50 }}
              />
            </StyledDialog.FormField>
            <StyledDialog.FormField label="Presentación">
              <TextField fullWidth size="small" sx={inputStyles}
                value={form.presentacion || ""}
                onChange={(e) => set("presentacion", e.target.value)}
                placeholder="Ej. Caja con 20 tabletas"
                inputProps={{ maxLength: 100 }}
              />
            </StyledDialog.FormField>

            <StyledDialog.FormField label="Forma farmacéutica">
              <TextField fullWidth size="small" sx={inputStyles}
                value={form.forma_farmaceutica || ""}
                onChange={(e) => set("forma_farmaceutica", e.target.value)}
                placeholder="Ej. Tableta, Jarabe, Cápsula"
                inputProps={{ maxLength: 50 }}
              />
            </StyledDialog.FormField>
            <StyledDialog.FormField label="Vía de administración">
              <TextField fullWidth size="small" sx={inputStyles}
                value={form.via_administracion || ""}
                onChange={(e) => set("via_administracion", e.target.value)}
                placeholder="Ej. oral, intramuscular, tópica"
                inputProps={{ maxLength: 30 }}
              />
            </StyledDialog.FormField>

            <StyledDialog.FormField label="Categoría">
              <Autocomplete
                freeSolo
                size="small"
                value={form.categoria || ""}
                inputValue={form.categoria || ""}
                onInputChange={(_, v) => set("categoria", v || "")}
                options={categorias}
                renderInput={(params) => (
                  <TextField {...params} sx={inputStyles}
                    placeholder="Ej. Antibiótico, Analgésico"
                    inputProps={{ ...params.inputProps, maxLength: 50 }}
                  />
                )}
              />
              <StyledDialog.HelperText>
                Escribe para buscar entre las existentes o teclea una nueva categoría.
              </StyledDialog.HelperText>
            </StyledDialog.FormField>
            <StyledDialog.FormField label="País">
              <FormControl fullWidth size="small">
                <Select
                  value={form.pais || "ALL"}
                  onChange={(e) => set("pais", e.target.value)}
                >
                  {paisesSoportados.map((p) => (
                    <MenuItem key={p.code} value={p.code}>
                      <Box component="span" sx={{ mr: 1 }}>{p.flag}</Box>
                      {p.label} ({p.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </StyledDialog.FormField>
          </Box>

          <Box sx={{ display: "flex", gap: 3, mt: 1, flexWrap: "wrap" }}>
            <FormControlLabel
              control={<Switch checked={!!form.requiere_receta} onChange={(e) => set("requiere_receta", e.target.checked)} />}
              label={<Typography sx={{ fontSize: 13 }}>Requiere receta</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={!!form.controlado} onChange={(e) => set("controlado", e.target.checked)} color="error" />}
              label={<Typography sx={{ fontSize: 13 }}>Controlado</Typography>}
            />
            {modo === "editar" && (
              <FormControlLabel
                control={<Switch checked={form.activo !== false} onChange={(e) => set("activo", e.target.checked)} color="success" />}
                label={<Typography sx={{ fontSize: 13 }}>Activo</Typography>}
              />
            )}
          </Box>
        </StyledDialog.FormSection>
      </StyledDialog.Body>
      <StyledDialog.Actions>
        <StyledDialog.CancelButton onClick={onClose} disabled={busy} />
        <StyledDialog.PrimaryButton
          loading={busy}
          disabled={!valid || busy}
          onClick={async () => {
            setBusy(true);
            const payload = {
              nombre_comercial: form.nombre_comercial.trim(),
              nombre_generico: form.nombre_generico.trim(),
              principio_activo: (form.principio_activo || "").trim() || null,
              presentacion: (form.presentacion || "").trim() || null,
              concentracion: (form.concentracion || "").trim() || null,
              forma_farmaceutica: (form.forma_farmaceutica || "").trim() || null,
              via_administracion: (form.via_administracion || "").trim() || null,
              laboratorio: (form.laboratorio || "").trim() || null,
              categoria: (form.categoria || "").trim() || null,
              pais: (form.pais || "ALL").trim().toUpperCase() || "ALL",
              requiere_receta: !!form.requiere_receta,
              controlado: !!form.controlado,
              ...(modo === "editar" ? { activo: form.activo !== false } : { activo: true }),
            };
            await onSave(payload);
            setBusy(false);
          }}
        >
          {modo === "crear" ? "Crear" : "Guardar"}
        </StyledDialog.PrimaryButton>
      </StyledDialog.Actions>
    </StyledDialog>
  );
}
